// @flow
import AttributeModel from "./AttributeModel";

import CompositeAttributeChildCollection from "./CompositeAttributeChildCollection";
import ConstraintCollection from "./input-constraints/ConstraintCollection";
import RangeConstraint from "./input-constraints/RangeConstraint";
import MandatoryRangeConstraint from "./input-constraints/MandatoryRangeConstraint";

import { ATTRIBUTE_WIDTH } from "../../constants";
import { IllegalArgumentException } from "../../exceptions";

import NumberAttributeModel from "./NumberAttributeModel";
import DatetimeAttributeModel from "./DatetimeAttributeModel";

import type { ModularUIModel, AttributeType, ModelOptions } from "../types";
import type ContentConfiguration from "../contentconfiguration/ContentConfiguration";
import type LinkModel from "../links/LinkModel";
import type ErrorResponse from "../error/ErrorResponse";

/**
 */
class CompositeAttributeModel extends AttributeModel {
  _children: CompositeAttributeChildCollection;

  /**
   */
  constructor(
    data: Object,
    contributions: Object,
    modelOptions?: ModelOptions,
  ) {
    super(data, contributions, modelOptions);

    this._children = CompositeAttributeChildCollection.create(
      data,
      contributions,
      modelOptions,
    );

    this.setChildrenOptionality();
  }

  /**
   */
  static isApplicableModel(contributions: Object): boolean {
    const type = contributions?.type;
    return type && (type === "composite" || type.includes("range"));
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    return this.children.getInitialChildModelLinks();
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>, errors: Array<ErrorResponse>) {
    this.children.setChildModels(models, errors);
  }

  /**
   */
  get type(): string {
    // temp fix for results and given answers on list result detail panel
    if (this.key === "results" || this.key === "givenAnswers") {
      return "composite";
    }

    return this.isRange ? "range" : "composite";
  }

  /**
   */
  get isRange(): boolean {
    return this.contributions?.type?.includes("range");
  }

  /**
   */
  get children(): CompositeAttributeChildCollection {
    return this._children;
  }

  /**
   */
  set children(children: CompositeAttributeChildCollection) {
    this._children = children;
  }

  /**
   */
  isRangeAttribute(attribute: AttributeType): boolean {
    return (
      attribute instanceof NumberAttributeModel ||
      attribute instanceof DatetimeAttributeModel
    );
  }

  /**
   */
  get start(): AttributeType | null {
    return this.children.all[0];
  }

  /**
   */
  get end(): AttributeType {
    return this.children.all[this.children.size - 1];
  }

  /**
   * Get input value of attribute
   */
  getInputValue(): string {
    return this.children.getInputValue();
  }

  /**
   * Check if at least one child has value
   */
  hasValue(): boolean {
    return this.children.some((child) => child.hasValue());
  }

  /**
   */
  get constraintCollection(): ConstraintCollection {
    const constraints = new ConstraintCollection();

    constraints.add(this._serverConstraints);
    constraints.add(this.addConstraints());

    return constraints;
  }

  /**
   * Used to programmatically overwrite the setting USE_CLIENTSIDE_VALIDATION
   */
  setUseClientsideValidation(enabled: boolean) {
    super._useClientsideValidation = enabled;

    this.children.all.forEach((child) =>
      child.setUseClientsideValidation(enabled),
    );
  }

  /**
   * Validate input
   */
  // eslint-disable-next-line no-unused-vars
  validate(value: string): boolean {
    // when client side validation is disabled, this attribute is always valid
    if (!this.useClientsideValidation()) {
      return true;
    }

    let isValid = this.children.validate();

    if (isValid) {
      isValid = this.constraintCollection.validate();
    }

    this.setIsValid(isValid);
    return this.getIsValid();
  }

  /**
   */
  get isValid(): boolean {
    return this.validate("");
  }

  /**
   * Add Date constraints for attribute
   */
  addConstraints(): ConstraintCollection {
    const constraints = new ConstraintCollection();

    const startAttribute = this.start;
    const endAttribute = this.end;
    if (this.isRange && startAttribute != null && endAttribute != null) {
      const allChildsEntered = this.children.every((child) => child.hasValue());
      if (allChildsEntered) {
        constraints.add(new RangeConstraint(startAttribute, endAttribute));
      }
    }

    if (this.isRange && this.mandatory && startAttribute !== null) {
      const allChildsOptional = this.children.every(
        (child) => !child.mandatory,
      );
      if (allChildsOptional) {
        constraints.add(
          new MandatoryRangeConstraint(startAttribute.type, this.children),
        );
      }
    }

    return constraints;
  }

  /**
   */
  hasServerErrors(): boolean {
    return (
      this.errorCollection.serverErrors.length > 0 ||
      this.children.some((child) => child.hasServerErrors())
    );
  }

  /**
   */
  hasErrors(): boolean {
    return (
      this.errorCollection.hasItems ||
      this.children.some((child) => child.hasErrors())
    );
  }

  /**
   * Reset attribute to empty string
   */
  reset() {
    this.children.reset();
  }

  /**
   */
  getChildByAttribute(attribute: AttributeType): ?AttributeType {
    return this.children.find((child) => {
      if (child.equals(attribute)) {
        return true;
      } else if (child instanceof CompositeAttributeModel) {
        return child.getChildByAttribute(attribute);
      }

      return false;
    });
  }

  /**
   */
  getChildByKey(key: string): ?AttributeType {
    return (
      this.children.find((child) => {
        if (child.key === key) {
          return true;
        } else if (child instanceof CompositeAttributeModel) {
          return child.getChildByKey(key);
        }

        return false;
      }) || null
    );
  }

  /**
   */
  mergeAttribute(oldAttribute: AttributeType) {
    this.concept = oldAttribute.concept;

    this.children.forEach((child) => {
      if (oldAttribute instanceof CompositeAttributeModel) {
        const withChildAttribute = oldAttribute.getChildByKey(child.key);
        if (withChildAttribute) {
          child.mergeAttribute(withChildAttribute);
        }
      }
    });
  }

  /**
   */
  update(
    value: string,
    changedAttribute: ?AttributeType,
  ): CompositeAttributeModel {
    if (!changedAttribute) {
      throw new IllegalArgumentException(
        "Composite attribute needs changed attribute on update call",
      );
    }

    this.updateLastModification();

    this.children.forEach((child) => {
      if (child instanceof CompositeAttributeModel) {
        child.update(value, changedAttribute);
      } else if (child.equals(changedAttribute)) {
        child.update(value);
      }
    });

    this.setChildrenOptionality();

    this.validate(value);

    return this;
  }

  /**
   */
  updateByAttribute(otherAttribute: AttributeType): void {
    if (otherAttribute instanceof CompositeAttributeModel) {
      otherAttribute.children.forEach((otherChild) => {
        const child = this.getChildByAttribute(otherChild);
        if (child) {
          child.updateByAttribute(otherChild);
        }
      });
    } else {
      console.error(
        `update of composite attribute with ${otherAttribute.type} not supported`,
      );
    }
  }

  /**
   */
  setChildrenOptionality() {
    const childrenAreOptional =
      !this.mandatory && this.children.every((child) => child.value === null);

    this.children.forEach((child) => {
      child.mandatory =
        childrenAreOptional || !child.configuredMandatory
          ? false
          : child.configuredMandatory;
    });
  }

  /**
   */
  get readonly(): boolean {
    return super.readonly || this.children.every((child) => child.readonly);
  }

  /**
   */
  set readonly(readonly: boolean) {
    super.readonly = readonly;
  }

  /**
   */
  get readonlyvalue(): string {
    return this.children.readonlyvalue;
  }

  /**
   */
  get initvalue(): any {
    return this.children.initvalue;
  }

  /**
   */
  getFormData(): { [string]: any } | null {
    return { [this.name]: this.value };
  }

  /**
   */
  getValue(): any {
    return this.children.value;
  }

  /**
   */
  getChildrenIds(): Array<string> {
    const values = this.data.value;
    if (Array.isArray(values)) {
      return values.map((val) => val._id);
    } else if (values != null) {
      return [values._id];
    }
    return [];
  }

  /**
   */
  get isResult(): boolean {
    return this._isResult;
  }

  /**
   */
  set isResult(isResult: boolean) {
    this._isResult = isResult;

    this.children.forEach((child) => {
      child.isResult = isResult;
    });
  }

  /**
   */
  indicateContentConfiguration(contentConfiguration: ContentConfiguration) {
    if (contentConfiguration) {
      this.children.forEach((child) => {
        child.indicateContentConfiguration(contentConfiguration);
      });
    }
  }

  /**
   */
  equals(otherAttribute: AttributeType): boolean {
    return (
      (this.key === otherAttribute.key &&
        this.parentKey === otherAttribute.parentKey) ||
      this.children.some(
        //$FlowFixMe[method-unbinding]
        (child) => child.equals?.(otherAttribute),
      )
    );
  }

  /**
   */
  get readonlyWidth(): $Keys<typeof ATTRIBUTE_WIDTH> {
    if (this.type === "composite") {
      return ATTRIBUTE_WIDTH.LARGE;
    }

    // range
    switch (this.start?.type) {
      case "datetime":
        return ATTRIBUTE_WIDTH.LARGE;
      case "timestamp":
        return ATTRIBUTE_WIDTH.EXTRA_LARGE;
      default:
        return ATTRIBUTE_WIDTH.MEDIUM;
    }
  }
}

export default CompositeAttributeModel;
